/**
 * RestartHistoryWidget - 显示来福引擎最近重启事件 + 代码 drift 检测
 *
 * 关联:
 *   后端 query: trpc.tradingProxy.recentRestarts (server/routers.ts)
 *   数据源: watchdog_logs WHERE message LIKE '[L3]%上报 Web%'
 *   audit: docs/AUDIT_L3_WATCHDOG_RESTART_OBSERVABILITY_20260524.md
 *
 * 使用:
 *   import { RestartHistoryWidget } from "@/components/RestartHistoryWidget";
 *   <RestartHistoryWidget />
 *
 * 显示:
 *   - 最近 24h 重启次数 (徽章)
 *   - 最新 t0_engine.py MD5 前 8 位
 *   - Drift 警告 (最近 5 次 MD5 不一致)
 *   - 展开查看历史表
 */
import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, CheckCircle2, RefreshCw, ChevronDown, ChevronUp, Activity } from "lucide-react";

const BJ_TZ = "Asia/Shanghai";

function formatBJTime(d: Date | string | number, withSec = false) {
  const date = new Date(d);
  return date.toLocaleString("zh-CN", {
    timeZone: BJ_TZ,
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    ...(withSec ? { second: "2-digit" } : {}),
  });
}

export function RestartHistoryWidget({ embedded = false }: { embedded?: boolean } = {}) {
  const [expanded, setExpanded] = useState(false);
  const { data, isLoading, refetch, isFetching } = trpc.tradingProxy.recentRestarts.useQuery(
    undefined,
    { refetchInterval: 60_000 }, // 每分钟自动刷新
  );

  if (isLoading) {
    return embedded ? (
      <Skeleton className="h-16 w-full" />
    ) : (
      <Card>
        <CardContent className="py-4">
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.events.length === 0) {
    if (embedded) {
      return <div className="text-xs text-muted-foreground">7 天内无重启记录</div>;
    }
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Activity className="h-4 w-4" />
            引擎重启历史
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-xs text-muted-foreground">7 天内无重启记录 (L3 patch 待生效)</div>
        </CardContent>
      </Card>
    );
  }

  const latest = data.events[0];

  // embedded 模式: 不带 Card 外壳, 不带 header (header 已由 caller 提供)
  if (embedded) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2 text-xs">
          {data.drift ? (
            <div className="flex items-center gap-1.5 text-amber-500">
              <AlertTriangle className="h-3.5 w-3.5" />
              <span>近 5 次重启代码 MD5 不一致 (drift detected)</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-emerald-500">
              <CheckCircle2 className="h-3.5 w-3.5" />
              <span>近 5 次重启代码 MD5 一致</span>
            </div>
          )}
          <Badge variant="outline" className="text-[10px] px-1.5">24h: {data.totalLast24h}</Badge>
        </div>

        <div className="flex items-center justify-between rounded-md border border-border/50 bg-muted/30 px-3 py-2 text-xs">
          <div className="space-y-0.5">
            <div className="text-muted-foreground">最近重启</div>
            <div className="font-medium">{formatBJTime(latest.at, true)}</div>
          </div>
          <div className="space-y-0.5 text-right">
            <div className="text-muted-foreground">t0_engine MD5</div>
            <code className="font-mono text-[11px]">{data.latestMd5 ?? "n/a"}</code>
          </div>
        </div>

        <Button
          size="sm"
          variant="ghost"
          className="w-full justify-center text-xs h-7"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? (
            <><ChevronUp className="h-3.5 w-3.5 mr-1" />收起历史</>
          ) : (
            <><ChevronDown className="h-3.5 w-3.5 mr-1" />展开 {data.events.length} 条历史</>
          )}
        </Button>

        {expanded && (
          <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
            {data.events.map((ev, idx) => {
              const prevMd5 = data.events[idx + 1]?.t0EngineMd5;
              const drifted = prevMd5 && ev.t0EngineMd5 && prevMd5 !== ev.t0EngineMd5;
              return (
                <div
                  key={`${ev.at}-${idx}`}
                  className={`flex items-center justify-between px-2 py-1 text-[11px] rounded ${
                    drifted ? "bg-amber-500/10 border border-amber-500/30" : "bg-muted/20"
                  }`}
                >
                  <span className="text-muted-foreground">{formatBJTime(ev.at, true)}</span>
                  <code className="font-mono">{ev.t0EngineMd5 ?? "n/a"}</code>
                  {drifted && <AlertTriangle className="h-3 w-3 text-amber-500 ml-1" />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            引擎重启历史
            <Badge variant="outline" className="text-xs">
              24h: {data.totalLast24h}
            </Badge>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => refetch()}
            disabled={isFetching}
            className="h-7 w-7 p-0"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pb-3">
        {/* Drift 状态 */}
        <div className="flex items-center justify-between gap-2 text-xs">
          {data.drift ? (
            <div className="flex items-center gap-1.5 text-amber-500">
              <AlertTriangle className="h-3.5 w-3.5" />
              <span>近 5 次重启代码 MD5 不一致 (drift detected)</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-emerald-500">
              <CheckCircle2 className="h-3.5 w-3.5" />
              <span>近 5 次重启代码 MD5 一致</span>
            </div>
          )}
        </div>

        {/* 最新重启信息 */}
        <div className="flex items-center justify-between rounded-md border border-border/50 bg-muted/30 px-3 py-2 text-xs">
          <div className="space-y-0.5">
            <div className="text-muted-foreground">最近重启</div>
            <div className="font-medium">{formatBJTime(latest.at, true)}</div>
          </div>
          <div className="space-y-0.5 text-right">
            <div className="text-muted-foreground">t0_engine MD5</div>
            <code className="font-mono text-[11px]">{data.latestMd5 ?? "n/a"}</code>
          </div>
        </div>

        {/* 展开/收起 */}
        <Button
          size="sm"
          variant="ghost"
          className="w-full justify-center text-xs h-7"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? (
            <>
              <ChevronUp className="h-3.5 w-3.5 mr-1" />
              收起历史
            </>
          ) : (
            <>
              <ChevronDown className="h-3.5 w-3.5 mr-1" />
              展开 {data.events.length} 条历史
            </>
          )}
        </Button>

        {expanded && (
          <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
            {data.events.map((ev, idx) => {
              const prevMd5 = data.events[idx + 1]?.t0EngineMd5;
              const drifted = prevMd5 && ev.t0EngineMd5 && prevMd5 !== ev.t0EngineMd5;
              return (
                <div
                  key={`${ev.at}-${idx}`}
                  className={`flex items-center justify-between px-2 py-1 text-[11px] rounded ${
                    drifted ? "bg-amber-500/10 border border-amber-500/30" : "bg-muted/20"
                  }`}
                >
                  <span className="text-muted-foreground">{formatBJTime(ev.at, true)}</span>
                  <code className="font-mono">{ev.t0EngineMd5 ?? "n/a"}</code>
                  {drifted && <AlertTriangle className="h-3 w-3 text-amber-500 ml-1" />}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
